import { useEffect, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import { useAuthContext } from "@/context/authContext";
import { useGlobalData } from "@/context/globalContext";
import { adminApi, AdminUpdateItemPayload } from "@/lib/client/admin";
import { useItemsStore } from "@/stores/itemsStore";
import { useUiStore } from "@/stores/uiStore";
import { typeSelection } from "@/lib/shared/constants";

const buildVendorFallback = (vendorId: number, label: string): SearchResultItem["vendor"] => {
  return {
    id: vendorId,
    label,
    value: null,
    link: null,
    region: null,
    type: null,
    description: null,
    included: 0,
    archiveactive: null,
    vendoractivity: null,
    affiliation: null,
    trustrating: null,
    instagram: null,
    coupons: [],
  };
};

export const useAdminItemEditor = (item: SearchResultItem | null) => {
  const { isAdmin } = useAuthContext();
  const { vendors } = useGlobalData();
  const { setItems } = useItemsStore((state) => state.actions);
  const items = useItemsStore((state) => state.items);
  const { setSlideInItem } = useUiStore((state) => state.actions);

  const vendorOptions = useMemo(
    () => (vendors || []).map((vendor) => ({ value: String(vendor.id), label: vendor.label })),
    [vendors]
  );

  const categoryOptions = useMemo(
    () => typeSelection.map((type) => ({ value: String(type.value), label: type.label })),
    []
  );

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: item?.title ?? "",
    price: item?.rawPrice ?? item?.price ?? "",
    priceusd: item?.priceusd ?? undefined,
    currency: item?.currency ?? "",
    vendorId: item?.vendor?.id ? String(item.vendor.id) : String(item?.vendorname ?? ""),
    sold: item?.isSold ?? item?.itemavailability !== 1,
    itemtype: item?.itemtype ?? "",
  });

  useEffect(() => {
    setForm({
      title: item?.title ?? "",
      price: item?.rawPrice ?? item?.price ?? "",
      priceusd: item?.priceusd ?? undefined,
      currency: item?.currency ?? "",
      vendorId: item?.vendor?.id ? String(item.vendor.id) : String(item?.vendorname ?? ""),
      sold: item?.isSold ?? item?.itemavailability !== 1,
      itemtype: item?.itemtype ?? "",
    });
  }, [
    item?.currency,
    item?.id,
    item?.isSold,
    item?.itemavailability,
    item?.itemtype,
    item?.price,
    item?.priceusd,
    item?.rawPrice,
    item?.title,
    item?.vendor?.id,
    item?.vendorname,
  ]);

  const resetForm = () => {
    if (!item) return;
    setForm({
      title: item.title ?? "",
      price: item.rawPrice ?? item.price ?? "",
      priceusd: item.priceusd ?? undefined,
      currency: item.currency ?? "",
      vendorId: item.vendor?.id ? String(item.vendor.id) : String(item.vendorname ?? ""),
      sold: item.isSold ?? item.itemavailability !== 1,
      itemtype: item.itemtype ?? "",
    });
  };

  const save = async () => {
    if (!item || !isAdmin) return;

    const baseVendorId = item.vendor?.id ? String(item.vendor.id) : String(item.vendorname ?? "");

    const payload: AdminUpdateItemPayload = {};
    const trimmedTitle = form.title.trim();
    const trimmedPrice = form.price.toString().trim();
    const trimmedCurrency = form.currency.trim().toUpperCase();
    const vendorIdValue = form.vendorId.toString().trim();
    const trimmedItemtype = form.itemtype.trim();

    if (trimmedTitle && trimmedTitle !== item.title) {
      payload.title = trimmedTitle;
    }
    if (trimmedPrice && trimmedPrice !== (item.rawPrice ?? item.price ?? "")) {
      payload.price = trimmedPrice;
    }
    if (form.priceusd !== undefined && form.priceusd !== item.priceusd) {
      payload.priceusd = form.priceusd;
    }
    if (trimmedCurrency && trimmedCurrency !== item.currency) {
      payload.currency = trimmedCurrency;
    }
    if (vendorIdValue && vendorIdValue !== baseVendorId) {
      payload.vendorname = vendorIdValue;
    }
    if (trimmedItemtype && trimmedItemtype !== item.itemtype) {
      payload.itemtype = trimmedItemtype;
    }
    if (form.sold !== item.isSold) {
      payload.itemavailability = form.sold ? 0 : 1;
    }

    if (!Object.keys(payload).length) {
      notifications.show({
        title: "No changes",
        message: "Update at least one field before saving.",
        color: "yellow",
      });
      return;
    }

    setSaving(true);
    const result = await adminApi.updateItem(item.id, payload);
    setSaving(false);

    if (!result.success) {
      notifications.show({
        title: "Update failed",
        message: result.error || "Failed to update item.",
        color: "red",
      });
      return;
    }

    const updated: SearchResultItem = { ...item };

    if (payload.title !== undefined) {
      updated.title = payload.title;
    }
    if (payload.price !== undefined) {
      updated.price = payload.price.toString();
      updated.rawPrice = payload.price.toString();
    }
    if (payload.priceusd !== undefined) {
      updated.priceusd = payload.priceusd;
    }
    if (payload.currency !== undefined) {
      updated.currency = payload.currency;
    }
    if (payload.vendorname !== undefined) {
      const vendorIdNum = parseInt(payload.vendorname.toString(), 10);
      const selectedVendor = vendorOptions.find((vendor) => vendor.value === String(vendorIdNum));
      updated.vendorname = payload.vendorname.toString();
      updated.vendor = selectedVendor
        ? buildVendorFallback(vendorIdNum, selectedVendor.label)
        : updated.vendor;
    }
    if (payload.itemavailability !== undefined) {
      updated.itemavailability = payload.itemavailability;
      updated.isSold = payload.itemavailability !== 1;
    }
    if (payload.itemtype !== undefined) {
      updated.itemtype = payload.itemtype;
    }

    const nextItems = items.map((entry) => (entry.id === updated.id ? updated : entry));
    setItems(nextItems);
    setSlideInItem(updated);

    notifications.show({
      title: "Item updated",
      message: "Changes saved successfully.",
      color: "green",
    });
  };

  const hideItem = async () => {
    if (!item || !isAdmin) return;

    const confirmed = window.confirm(
      `Are you sure you want to hide "${item.title}"? It will stop showing up in search, ` +
        `carousels, and saved-search alerts. The listing itself is kept, not deleted.`
    );
    if (!confirmed) return;

    setSaving(true);
    const result = await adminApi.hideItem(item.id);
    setSaving(false);

    if (!result.success) {
      notifications.show({
        title: "Hide failed",
        message: result.error || "Failed to hide item.",
        color: "red",
      });
      return;
    }

    const nextItems = items.filter((entry) => entry.id !== item.id);
    setItems(nextItems);

    notifications.show({
      title: "Item hidden",
      message: "The item has been hidden from customers.",
      color: "green",
    });

    return true;
  };

  return {
    isAdmin,
    form,
    setForm,
    resetForm,
    save,
    hideItem,
    saving,
    vendorOptions,
    categoryOptions,
  };
};
